import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useFormik } from 'formik'
import {
  FormControl,
  IconButton,
  TextField as Input,
  InputAdornment,
  InputLabel,
  OutlinedInput,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'

import api from 'configs/api'
import { alertDanger, alertSuccess } from 'utils/toast'

import { Button, Loading, CodeInput } from 'components'

import logo from 'assets/images/starbem-doctor.svg'
import * as S from './styles'
import { schemaEmail, schemaPassword } from './validations'

type Step = 'email' | 'code' | 'password'

const ForgotPassword: React.FC = () => {
  const history = useHistory()

  const [step, setStep] = useState<Step>('email')
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [userId, setUserId] = useState('')
  const [code, setCode] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  useEffect(() => {
    if (resendCooldown <= 0) return undefined
    const id = window.setInterval(() => {
      setResendCooldown((s) => Math.max(0, s - 1))
    }, 1000)
    return () => clearInterval(id)
  }, [resendCooldown > 0])

  const emailForm = useFormik({
    initialValues: { email: '' },
    validationSchema: schemaEmail,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        await api.post('/doctors/ms/v1/user/forgot-password', {
          email: values.email,
        })
        setEmail(values.email)
        setStep('code')
        setResendCooldown(60)
        alertSuccess('Código enviado para seu email!')
      } catch (err: any) {
        alertDanger(
          err?.response?.data?.message ||
            'Não foi possível enviar o código. Verifique o email.'
        )
      } finally {
        setLoading(false)
      }
    },
  })
  const handleCodeSubmit = async () => {
    try {
      const { data } = await api.post('/doctors/ms/v1/user/validate-token', {
        email,
        token: code,
      })
      setUserId(data?.user_id)
      setStep('password')
    } catch (err: any) {
      alertDanger(
        err?.response?.data?.message || 'Código inválido ou expirado.'
      )
    }
  }

  const passwordForm = useFormik({
    initialValues: { password: '', confirmPassword: '' },
    validationSchema: schemaPassword,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        await api.post('/doctors/ms/v1/user/reset-password-token', {
          user_id: userId,
          token: code,
          password: values.password,
        })
        alertSuccess('Senha alterada com sucesso!')
        history.push('/')
      } catch (err: any) {
        alertDanger(
          err?.response?.data?.message ||
            'Não foi possível alterar a senha. Tente novamente.'
        )
      } finally {
        setLoading(false)
      }
    },
  })

  const handleResend = async () => {
    setLoading(true)
    try {
      await api.post('/doctors/ms/v1/user/forgot-password', { email })
      setResendCooldown(60)
      alertSuccess('Código reenviado!')
    } catch (err: any) {
      alertDanger(
        err?.response?.data?.message || 'Não foi possível reenviar o código.'
      )
    } finally {
      setLoading(false)
    }
  }

  const renderStepEmail = () => (
    <>
      <S.Title>Recuperar senha</S.Title>
      <S.Subtitle>
        Informe seu email para receber o código de recuperação.
      </S.Subtitle>
      <S.Form onSubmit={emailForm.handleSubmit}>
        <Input
          label="Email"
          type="email"
          name="email"
          variant="outlined"
          color="secondary"
          onChange={emailForm.handleChange}
          value={emailForm.values.email}
          error={!!emailForm.errors.email}
          helperText={emailForm.errors.email}
        />
        <S.AreaButton>
          <Button
            color="primary"
            type="submit"
            disabled={!emailForm.isValid || loading}
            styles={{ width: '100%', height: 50 }}
          >
            Enviar código
          </Button>
        </S.AreaButton>
      </S.Form>
    </>
  )

  const renderStepCode = () => (
    <>
      <S.Title>Verificar código</S.Title>
      <S.Subtitle>
        Digite o código enviado para <strong>{email}</strong>.
      </S.Subtitle>
      <S.Form>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <CodeInput onChange={setCode} qtd={['', '', '', '']} />
        </div>
        <S.AreaButton>
          <Button
            color="primary"
            type="button"
            disabled={code.length !== 4 || loading}
            styles={{ width: '100%', height: 50 }}
            onClick={handleCodeSubmit}
          >
            Validar código
          </Button>
          <Button
            color="default"
            type="button"
            outline
            onClick={handleResend}
            disabled={loading || resendCooldown > 0}
            styles={{ width: '100%', height: 50 }}
          >
            {resendCooldown > 0
              ? `Reenviar código (${resendCooldown}s)`
              : 'Reenviar código'}
          </Button>
        </S.AreaButton>
      </S.Form>
    </>
  )

  const renderStepPassword = () => (
    <>
      <S.Title>Nova senha</S.Title>
      <S.Subtitle>Defina sua nova senha de acesso.</S.Subtitle>
      <S.Form onSubmit={passwordForm.handleSubmit}>
        <FormControl fullWidth variant="outlined">
          <InputLabel htmlFor="new-password" color="secondary">
            Nova senha
          </InputLabel>
          <OutlinedInput
            id="new-password"
            label="Nova senha"
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={passwordForm.values.password}
            onChange={passwordForm.handleChange}
            color="secondary"
            error={!!passwordForm.errors.password}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onMouseDown={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
          />
          {passwordForm.errors.password && (
            <span
              style={{
                color: '#f44336',
                fontSize: 12,
                marginTop: 4,
                marginLeft: 14,
              }}
            >
              {passwordForm.errors.password}
            </span>
          )}
        </FormControl>
        <FormControl fullWidth variant="outlined">
          <InputLabel htmlFor="confirm-password" color="secondary">
            Confirmar nova senha
          </InputLabel>
          <OutlinedInput
            id="confirm-password"
            label="Confirmar nova senha"
            type={showConfirmPassword ? 'text' : 'password'}
            name="confirmPassword"
            value={passwordForm.values.confirmPassword}
            onChange={passwordForm.handleChange}
            color="secondary"
            error={!!passwordForm.errors.confirmPassword}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle confirm password visibility"
                  onMouseDown={() =>
                    setShowConfirmPassword(!showConfirmPassword)
                  }
                  edge="end"
                >
                  {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
          />
          {passwordForm.errors.confirmPassword && (
            <span
              style={{
                color: '#f44336',
                fontSize: 12,
                marginTop: 4,
                marginLeft: 14,
              }}
            >
              {passwordForm.errors.confirmPassword}
            </span>
          )}
        </FormControl>
        <S.AreaButton>
          <Button
            color="primary"
            type="submit"
            disabled={!passwordForm.isValid || loading}
            styles={{ width: '100%', height: 50 }}
          >
            Alterar senha
          </Button>
        </S.AreaButton>
      </S.Form>
    </>
  )

  return (
    <S.Container>
      {loading && <Loading title="Aguarde..." />}
      <S.Logo>
        <img src={logo} alt="Starbem" />
      </S.Logo>
      <S.Card>
        <S.StepIndicator>
          <S.StepDot active={step === 'email'} />
          <S.StepDot active={step === 'code'} />
          <S.StepDot active={step === 'password'} />
        </S.StepIndicator>
        {step === 'email' && renderStepEmail()}
        {step === 'code' && renderStepCode()}
        {step === 'password' && renderStepPassword()}
        <S.BackLink type="button" onClick={() => history.push('/')}>
          Voltar para o login
        </S.BackLink>
      </S.Card>
    </S.Container>
  )
}

export default ForgotPassword
